import SearchStart from './SearchStart';
import ShortlistWorkspace from '../TalentAgent/ShortlistWorkspace';
import { getSourcingView } from '../../utils/sidebarPreferences';
import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import { TALENT_API_BASE, loadTalentCredits } from './talentAccount';
import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import './TalentSearch.css';
// Using FontAwesome kit via <i> tags (consistent with rest of app)
import { database, auth } from '../../firebase';
import { ref, get, set, onValue } from 'firebase/database';
import { useNavigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import UnlockedProfiles from './UnlockedProfiles';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import '../../styles/tutorial.css';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAccessControl } from '../../hooks/useAccessControl';
import useCompanyUID from '../../hooks/useCompanyUID';
import { appendUniqueProfiles } from './talentResults';
import TalentPosition from './TalentPosition';
import TalentMatches from './TalentMatches';
import { connectorOrigin, connectorRequest } from '../TalentConnector/connectorApi';

async function talentAuthConfig() {
  const token = await auth.currentUser?.getIdToken?.();
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
}

const TalentSearch = () => {
  useUiLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  // i18n hooks
  const { t, i18n } = useTranslation('sourcing');
  const { isRTL, language } = useLanguage();
  const { companyUID, loading: companyUidLoading } = useCompanyUID();
  const { subscription, planId } = useAccessControl();

  // Set page title
  useEffect(() => {
    document.title = "JOBSEEQR | Talent Search Engine";
  }, []);

  const { searchState: savedSearchState } = location.state || {};
  // Older snapshots predate signup requirement checks; keep the query for a fresh search.
  const searchState = savedSearchState?.qualityVersion === 3 && !TalentMatches.hasPending(savedSearchState.profiles || [])
    ? savedSearchState : { ...savedSearchState, profiles: [], continuation: null };

  const [searchText, setSearchText] = useState(searchState?.searchText || '');
  const [hasUnlimitedPlan, setHasUnlimitedPlan] = useState(false);
  // Suggestions with translation keys (Saudi, Qatar, Kuwait)
  const suggestions = useMemo(() => [
    {
      label: t('suggestions.saudiDev', 'Software Engineer in Saudi Arabia'),
      detailedQuery: t('suggestions.saudiDevDetail', 'Senior Software Engineer in Riyadh, Saudi Arabia skilled in React, Node.js, and cloud architectures with 5+ years of experience.'),
    },
    {
      label: t('suggestions.qatarSales', 'B2B Sales Specialist in Qatar'),
      detailedQuery: t('suggestions.qatarSalesDetail', 'Sales Representative in Doha, Qatar with expertise in business-to-business (B2B) sales, client acquisition, and enterprise relationship management.'),
    },
    {
      label: t('suggestions.kuwaitHr', 'HR & Talent Manager in Kuwait'),
      detailedQuery: t('suggestions.kuwaitHrDetail', 'HR Manager in Kuwait City, Kuwait with experience in talent acquisition, employee relations, and workforce planning.'),
    },
    {
      label: t('suggestions.saudiMarketing', 'Digital Marketing Manager in Saudi Arabia'),
      detailedQuery: t('suggestions.saudiMarketingDetail', 'Digital Marketing Manager in Jeddah, Saudi Arabia experienced in growth marketing, brand strategy, and multi-channel campaign management.'),
    },
  ], [t]);
  const [isLoading, setIsLoading] = useState(false);
  const [profiles, setProfiles] = useState(searchState?.profiles || []);
  const [lebanonDescription, setLebanonDescription] = useState(searchState?.lebanonDescription || '');
  const scrollRef = useRef(null);
  const moreResultsRef = useRef(null);
  const pageRequestRef = useRef(false);
  const matchRequestRef = useRef(new AbortController());
  const searchGeneration = useRef(0);
  const restoredScroll = useRef(false);
  const [continuation, setContinuation] = useState(searchState?.continuation || null);
  const [activeQuery, setActiveQuery] = useState(searchState?.activeQuery || searchState?.searchText || '');
  const [loadingMore, setLoadingMore] = useState(false);
  const [pageError, setPageError] = useState('');

  // Starter screen auto-expand & filtering mode expand/collapse controls
  const starterTextareaRef = useRef(null);
  const [isFilterBoxExpanded, setIsFilterBoxExpanded] = useState(false);

  // Auto-expand textarea to max-height on the starter screen only (never when results are active)
  useEffect(() => {
    const textarea = starterTextareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    const nextHeight = Math.min(Math.max(180, textarea.scrollHeight), 460);
    textarea.style.height = `${nextHeight}px`;
  }, [searchText, profiles.length, continuation]);

  useEffect(() => () => { searchGeneration.current += 1; matchRequestRef.current.abort(); }, []);

  // Shortlists now render as a full workspace within the Sourcing route.
  const [isShortlistsPopupOpen, setIsShortlistsPopupOpen] = useState(() => getSourcingView(location.search) === 'shortlists');
  const [shortlists, setShortlists] = useState([]);
  const [shortlistsLoading, setShortlistsLoading] = useState(true);
  const [shortlistsError, setShortlistsError] = useState('');
  const [shortlistsRetry, setShortlistsRetry] = useState(0);

  const [userUid, setUserUid] = useState(null);
  const [selectedShortlist, setSelectedShortlist] = useState(searchState?.selectedShortlist || null);

  const [isDownloading, setIsDownloading] = useState(false);
  const [hasSearched, setHasSearched] = useState(profiles.length > 0);

  // State for zero credits popup
  const [showZeroCreditsPopup, setShowZeroCreditsPopup] = useState(false);

  // for views
  const [remainingCredits, setRemainingCredits] = useState(0);
  const [currentView, setCurrentView] = useState(() => getSourcingView(location.search) === 'unlocked' ? 'unlocked' : 'search');
  const navigationViewRef = useRef(getSourcingView(location.search));
  const [unlockedProfiles, setUnlockedProfiles] = useState([]);
  const [creditsLoading, setCreditsLoading] = useState(true);
  const [creditsError, setCreditsError] = useState(false);
  const [creditsRetry, setCreditsRetry] = useState(0);
  const [hideViewed, setHideViewed] = useState(false);
  const [viewedProfileIds, setViewedProfileIds] = useState([]);
  const accountUid = companyUID || (!companyUidLoading ? userUid : '');
  const hasUnlimitedSubscription = Boolean(
    subscription?.status === 'unlimited' ||
    subscription?.unlimited === true ||
    subscription?.credits === 'unlimited' ||
    subscription?.tseCredits === 'unlimited' ||
    subscription?.features?.creditsPerMonth === 'unlimited' ||
    planId === 'tse_custom' ||
    planId === 'bundle_custom'
  );

  // Sidebar links and browser history select existing views without discarding search results.
  useEffect(() => {
    const view = getSourcingView(location.search);
    navigationViewRef.current = view;
    setCurrentView(view === 'unlocked' ? 'unlocked' : 'search');
    setIsShortlistsPopupOpen(view === 'shortlists');
    if (view === 'shortlists' || view === 'unlocked') setShowZeroCreditsPopup(false);
    if (view === 'shortlists' && !new URLSearchParams(location.search).has('shortlist')) {
      setSelectedShortlist(null);
    }
  }, [location.search, location.key]);
  const closeShortlists = () => {
    setIsShortlistsPopupOpen(false);
    if (getSourcingView(location.search) === 'shortlists') {
      const params = new URLSearchParams(location.search);
      params.delete('shortlist');
      params.set('view', 'search');
      navigate({ pathname: '/sourcing', search: `?${params}` }, { replace: true, state: location.state });
    }
  };
  useEffect(() => {
    const controller = new AbortController();
    const requested = new URLSearchParams(location.search).get('shortlist');
    if (!connectorOrigin || !userUid) return () => controller.abort();
    // Reading the unified list also imports older connector-only lists once.
    // Firebase's native listener then receives those rows automatically.
    connectorRequest(auth.currentUser, 'shortlists', { signal: controller.signal })
      .then(async data => {
        if (!requested || controller.signal.aborted) return;
        const direct = (data.shortlists || []).find(row => row.shortlist_id === requested);
        if (direct) { setSelectedShortlist(direct.name); return; }
        // Resolve an older hashed link after migration.
        const resolved = await connectorRequest(auth.currentUser,
          `shortlist?shortlist_id=${encodeURIComponent(requested)}`, { signal: controller.signal });
        if (!controller.signal.aborted) setSelectedShortlist(resolved.name);
      })
      .catch(() => {});
    return () => controller.abort();
  }, [userUid, location.search]);
  useEffect(() => {
    const requested = new URLSearchParams(location.search).get('shortlist');
    if (requested && shortlists.some(item => item.name === requested)) setSelectedShortlist(requested);
  }, [location.search, shortlists]);

  const API_BASE_URL = TALENT_API_BASE;

  function safeFirebaseKey(key) {
    return key.replace(/@/g, '_at_').replace(/\./g, '_dot_');
  }

  function restoreFirebaseKey(key) {
    return key.replace(/_at_/g, '@').replace(/_dot_/g, '.');
  }

  useLayoutEffect(() => {
    // Run restoration after profiles are rendered
    const restoreScrollPosition = () => {
      if (!restoredScroll.current && scrollRef.current && profiles.length > 0) {
        restoredScroll.current = true;
        const savedScrollPosition = sessionStorage.getItem('talentScrollPosition');
        if (savedScrollPosition !== null) {
          // Use requestAnimationFrame to ensure DOM is fully rendered
          requestAnimationFrame(() => {
            if (scrollRef.current) {
              scrollRef.current.scrollTop = Number(savedScrollPosition);
            }
          });
        }
      }
    };

    restoreScrollPosition();
  }, [profiles.length]);

  useEffect(() => {
    const element = scrollRef.current;
    return () => {
      if (element) {
        sessionStorage.setItem('talentScrollPosition', element.scrollTop.toString());
      }
    };
  }, []);

  // After user is authenticated, get UID and fetch shortlists
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserUid(user.uid);
      } else {
        setUserUid(null);
        window.location.href = 'https://www.jobseeqr.co';
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (searchText.trim() === '') {
      setHasSearched(false);
    }
  }, [searchText]);


  useEffect(() => {
    if (!accountUid) return;
    const revealedProfilesRef = ref(database, `companies/${accountUid}/revealed_profiles`);
    const unsubscribe = onValue(revealedProfilesRef, (snapshot) => {
      const data = snapshot.val();
      const ids = data ? Object.keys(data) : [];
      setViewedProfileIds(ids);
    });
    return () => unsubscribe();
  }, [accountUid]);

  const fetchShortlists = useCallback((uid) => {
    const userId = uid || accountUid;
    if (!userId) return;
    const shortlistsRef = ref(database, `companies/${userId}/tal_shortlists`);
    return onValue(
      shortlistsRef,
      (snapshot) => {
        setShortlistsLoading(false);
        setShortlistsError('');
        const data = snapshot.val();
        if (data) {
          const fetchedShortlists = Object.keys(data).map((shortlistName) => {
            const profilesObj = data[shortlistName].profiles || {};
            const profilesCount = Object.keys(profilesObj).length;
            return {
              ...data[shortlistName],
              profiles: undefined,
              name: shortlistName,
              profilesCount,
              reviewCounts: Object.values(profilesObj).reduce((counts, person) => {
                const status = person.review?.status || 'review';
                counts[status] = (counts[status] || 0) + 1;
                return counts;
              }, { review: 0, selected: 0, passed: 0 }),
              previews: Object.values(profilesObj).slice(0, 3).map(person => ({
                name: person.username || person.name || '',
                role: person.current_role || person.jobTitle || '',
              })),
            };
          });
          setShortlists(fetchedShortlists);
        } else {
          setShortlists([]);
        }
      },
      (error) => {
        setShortlistsLoading(false);
        setShortlistsError('Could not load shortlists.');
        console.error('Error fetching shortlists:', error);
      }
    );
  }, [accountUid]);

  useEffect(() => {
    if (!accountUid) return;
    return fetchShortlists(accountUid);
  }, [accountUid, fetchShortlists, shortlistsRetry]);

  const handleShortlistsClick = () => {
    navigate('/sourcing?view=shortlists', { state: location.state });
  };
  const selectShortlist = name => {
    setSelectedShortlist(name);
    const params = new URLSearchParams(location.search);
    params.set('view', 'shortlists');
    if (name) params.set('shortlist', name);
    else params.delete('shortlist');
    navigate({ pathname: '/sourcing', search: `?${params}` }, { state: location.state });
  };

  // Zero-credits popup handling
  useEffect(() => {
    const handlePopup = async () => {
      if (hasUnlimitedPlan) {
        setShowZeroCreditsPopup(false);
        return;
      }

      if (location.state?.justRanOut && accountUid) {
        setShowZeroCreditsPopup(true);
        try {
          await axios.post(
            `${API_BASE_URL}/update_popup_shown`,
            { shown: true },
            { params: { user_uid: accountUid } }
          );
        } catch (error) {
          console.error('Error updating popup shown status:', error);
        }
      }
    };

    handlePopup();
  }, [location.state, accountUid, hasUnlimitedPlan]);

  const prepareProfilesForDisplay = useCallback(async (profilesData) => {
    const formatName = (fullName) => {
      const nameParts = fullName.split(' ');
      if (nameParts.length > 1) {
        return `${nameParts[0]}`; // First name
      }
      return fullName; // In case there's only one name part
    };

    return Promise.all(
      profilesData.map(async (profile) => {
        const userUid = profile.metadata.user_uid;
        const metadata = profile.metadata;

        let username = metadata.name || 'Unknown';
        let linkedInURL = metadata.linkedin_url || 'Unknown';
        let openToWork = metadata.openToWork !== undefined ? metadata.openToWork : false;

        // fetch from firebase if missing fields
        if (userUid && (
          username === 'Unknown' ||
          metadata.openToWork === undefined ||
          linkedInURL === 'Unknown'
        )) {
          const userRef = ref(database, `people/${userUid}`);
          const userSnapshot = await get(userRef);

          if (username === 'Unknown') {
            username = userSnapshot.child('username').exists()
              ? userSnapshot.child('username').val()
              : 'Unknown';
          }
          if (metadata.openToWork === undefined) {
            openToWork = userSnapshot.child('openToWork').exists()
              ? userSnapshot.child('openToWork').val()
              : false;
          }
          if (linkedInURL === 'Unknown' || metadata.linkedin_url === undefined) {
            linkedInURL = userSnapshot.child('linkedin').exists()
              ? userSnapshot.child('linkedin').val()
              : '';
          }
        }

        const capitalizedUsername = username.replace(/\b\w/g, (char) => char.toUpperCase());
        const formattedName = formatName(capitalizedUsername);

        return {
          ...profile,
          metadata: {
            ...profile.metadata,
            username: formattedName,
            fullUsername: capitalizedUsername,
            openToWork: openToWork,
            linkedInURL: linkedInURL,
          },
        };
      })
    );
  }, []);

  const handleSearch = async () => {
    if (searchText.trim() === '') return;
    if (!accountUid) return;

    const generation = ++searchGeneration.current;
    matchRequestRef.current.abort();
    const matchController = new AbortController();
    matchRequestRef.current = matchController;
    const query = searchText.trim();
    pageRequestRef.current = false;
    setContinuation(null);
    setLoadingMore(false);
    setPageError('');
    restoredScroll.current = true;
    sessionStorage.removeItem('talentScrollPosition');
    setIsLoading(true);
    setProfiles([]);
    setLebanonDescription('');

    try {
      const authConfig = await talentAuthConfig();
      // Search JOBSEEQR signups and the configured external provider.
      const [profilesResponse, descriptionResponse] = await Promise.all([
        axios.post(`${API_BASE_URL}/v2/search_page`, {
          query,
          limit: 100,
          user_uid: accountUid,
          query_language: language || i18n.language,
        }, authConfig),
        axios.post(`${API_BASE_URL}/generate_description`, { query: searchText }),
      ]);

      const profilesData = profilesResponse.data.profiles;
      const descriptionData = descriptionResponse.data;

      const matchedProfiles = await TalentMatches.prepareProfiles(profilesData, API_BASE_URL, { signal: matchController.signal });
      const profilesWithNames = await prepareProfilesForDisplay(matchedProfiles);
      if (generation !== searchGeneration.current) return;
      setContinuation(profilesResponse.data.continuation || null);
      setActiveQuery(query);

      setProfiles(profilesWithNames);
      setLebanonDescription(descriptionData.description);
      setHasSearched(true);
    } catch (error) {
      if (generation !== searchGeneration.current) return;
      console.error('Error fetching profiles or description:', error);
      alert(error.response?.data?.detail || 'Talent search failed. Please try again.');
    } finally {
      if (generation === searchGeneration.current) setIsLoading(false);
    }
  };

  const loadNextPage = useCallback(async () => {
    if (!continuation || pageRequestRef.current || isLoading) return;
    const generation = searchGeneration.current;
    const matchController = matchRequestRef.current;
    pageRequestRef.current = true;
    setLoadingMore(true);
    setPageError('');
    try {
      const response = await axios.post(`${API_BASE_URL}/v2/search_page`, {
        query: activeQuery, continuation, user_uid: accountUid,
      }, await talentAuthConfig());
      if (generation !== searchGeneration.current) return;
      const matchedProfiles = await TalentMatches.prepareProfiles(response.data.profiles, API_BASE_URL, { signal: matchController.signal });
      const nextProfiles = await prepareProfilesForDisplay(matchedProfiles);
      if (generation !== searchGeneration.current) return;
      setProfiles(previous => appendUniqueProfiles(previous, nextProfiles));
      setContinuation(response.data.continuation || null);
    } catch (error) {
      if (generation === searchGeneration.current) {
        setPageError(error.response?.data?.detail || t('results.loadError', 'Could not load more results.'));
      }
    } finally {
      if (generation === searchGeneration.current) {
        pageRequestRef.current = false;
        setLoadingMore(false);
      }
    }
  }, [continuation, isLoading, activeQuery, accountUid, prepareProfilesForDisplay, t]);

  useEffect(() => {
    if (!continuation || isLoading || loadingMore || pageError || currentView !== 'search') return undefined;
    const target = moreResultsRef.current;
    if (!target) return undefined;
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) loadNextPage();
    }, { root: scrollRef.current, rootMargin: '300px' });
    observer.observe(target);
    return () => observer.disconnect();
  }, [continuation, isLoading, loadingMore, pageError, currentView, profiles.length, hideViewed, loadNextPage]);

  const handleSuggestionClick = (suggestionLabel) => {
    const selectedSuggestion = suggestions.find((s) => s.label === suggestionLabel);
    if (selectedSuggestion) {
      setSearchText(selectedSuggestion.detailedQuery);
    } else {
      setSearchText(suggestionLabel);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const fetchRemainingCredits = async () => {
      if (companyUidLoading || !accountUid) return;
      setCreditsLoading(true);
      setCreditsError(false);
      setShowZeroCreditsPopup(false);
      try {
        const credits = await loadTalentCredits(accountUid, hasUnlimitedSubscription);
        if (cancelled) return;
        setHasUnlimitedPlan(credits.unlimited);
        setRemainingCredits(credits.remaining);
        if (!navigationViewRef.current) setCurrentView(credits.remaining > 0 ? 'search' : 'unlocked');
        setShowZeroCreditsPopup(credits.remaining <= 0 && !['shortlists', 'unlocked'].includes(navigationViewRef.current));
      } catch (error) {
        if (!cancelled) setCreditsError(true);
        console.error('Error fetching remaining credits:', error);
      } finally {
        if (!cancelled) setCreditsLoading(false);
      }
    };
    fetchRemainingCredits();
    return () => { cancelled = true; };
  }, [accountUid, companyUidLoading, hasUnlimitedSubscription, creditsRetry]);

  const getRankingBadgeProps = (index) => ({
    badgeLabel: t('results.rank', 'Rank #{{rank}}', { rank: index + 1 }),
    badgeColor: ['#d4af37', '#C0C0C0', '#CD7F32'][index] || '#568ded',
  });

  useEffect(() => {
    const fetchUnlockedProfiles = async () => {
      if (currentView !== 'unlocked' || !accountUid) return;

      try {
        const revealedProfilesRef = ref(
          database,
          `companies/${accountUid}/revealed_profiles`
        );
        const snapshot = await get(revealedProfilesRef);
        const revealedProfiles = snapshot.val();
        if (!revealedProfiles) {
          setUnlockedProfiles([]);
          return;
        }

        // convert them back into real Pinecone IDs
        const safeKeys = Object.keys(revealedProfiles);
        const profileIds = safeKeys.map(restoreFirebaseKey);

        if (profileIds.length === 0) {
          setUnlockedProfiles([]);
          return;
        }

        // fetch from Pinecone
        const response = await axios.post(
          `${API_BASE_URL}/get_profiles`,
          { profileIds },
          await talentAuthConfig()
        );
        const fetchedUnlockedProfiles = response.data;

        // fill in any missing metadata from Firebase
        const updatedProfiles = await Promise.all(
          fetchedUnlockedProfiles.map(async (profile) => {
            const uid = profile.metadata.user_uid;
            const metadata = profile.metadata;
            let username = metadata.username || metadata.name || 'Unknown';
            let openToWork =
              metadata.openToWork !== undefined ? metadata.openToWork : false;
            if (uid && (username === 'Unknown' || metadata.openToWork === undefined)) {
              const userRef = ref(database, `people/${uid}`);
              const userSnap = await get(userRef);
              if (username === 'Unknown') {
                username = userSnap.child('username').exists()
                  ? userSnap.child('username').val()
                  : 'Unknown';
              }
              if (metadata.openToWork === undefined) {
                openToWork = userSnap.child('openToWork').exists()
                  ? userSnap.child('openToWork').val()
                  : false;
              }
            }
            const capitalized = username.replace(/\b\w/g, (c) =>
              c.toUpperCase()
            );
            return {
              ...profile,
              metadata: {
                ...profile.metadata,
                username: capitalized,
                openToWork,
              },
            };
          })
        );

        setUnlockedProfiles(updatedProfiles);
      } catch (error) {
        console.error('Error fetching unlocked profiles:', error);
        setUnlockedProfiles([]);
      }
    };

    fetchUnlockedProfiles();
  }, [accountUid, currentView]);

  // Helper function to check if profile has contact info (email or phone)
  // Returns: true if has contact, false if DEFINITELY no contact, null if uncertain
  const hasContactInfo = async (profile) => {
    const userUid = profile.metadata?.user_uid;
    if (!userUid) {
      console.warn('[Contact Check] No user_uid in profile, proceeding with unlock');
      return null; // Uncertain - proceed with unlock to be safe
    }

    // First check metadata
    const hasEmailInMetadata = profile.metadata.email &&
      profile.metadata.email !== 'nan' &&
      profile.metadata.email !== '' &&
      profile.metadata.email !== 'No Email On File';
    const hasPhoneInMetadata = profile.metadata.phoneNumber &&
      profile.metadata.phoneNumber !== 'No Phone Number' &&
      profile.metadata.phoneNumber !== '';

    if (hasEmailInMetadata || hasPhoneInMetadata) {
      console.log('[Contact Check] Found contact info in metadata');
      return true;
    }

    // If not in metadata, check Firebase
    try {
      const [emailResponse, phoneSnapshot] = await Promise.all([
        axios.post(
          'https://us-central1-jobseeqr.cloudfunctions.net/getUserEmail',
          { uid: userUid }
        ).catch((err) => {
          console.warn('[Contact Check] Email fetch failed:', err.message);
          return { data: { email: null } };
        }),
        get(ref(database, `people/${userUid}/phoneNumber`)).catch((err) => {
          console.warn('[Contact Check] Phone fetch failed:', err.message);
          return null;
        })
      ]);

      const email = emailResponse?.data?.email;
      const phone = phoneSnapshot?.exists() ? phoneSnapshot.val() : null;

      const hasValidEmail = email && email !== 'No Email On File' && email !== 'nan' && email !== '';
      const hasValidPhone = phone && phone !== 'No Phone Number' && phone !== '';

      if (hasValidEmail || hasValidPhone) {
        console.log('[Contact Check] Found contact info in Firebase');
        return true;
      }

      // Only return false if we successfully checked both and found nothing
      console.log('[Contact Check] No contact info found');
      return false;
    } catch (error) {
      console.error('[Contact Check] Error checking contact info:', error);
      // On error, return null (uncertain) - proceed with unlock to be safe
      return null;
    }
  };

  const handleProfileClick = async (profile) => {
    if (profile.metadata?.connector_candidate_id && selectedShortlist) {
      navigate(`/talent/profile?shortlist_id=${encodeURIComponent(selectedShortlist)}&candidate_id=${encodeURIComponent(profile.metadata.connector_candidate_id)}&from=sourcing`);
      return;
    }
    console.log('Clicked Profile:', profile);
    console.log('Clicked Profile experience:', profile.metadata.experience);

    // Save the gallery vertical scroll position in sessionStorage
    if (scrollRef.current) {
      sessionStorage.setItem('talentScrollPosition', scrollRef.current.scrollTop.toString());
    }

    const isExternalProfile = ['signalhire', 'crustdata'].includes(profile?.metadata?.source) ||
      profile?.metadata?.pinecone_id?.startsWith('sh_') || profile?.metadata?.pinecone_id?.startsWith('cd_');

    if (isExternalProfile) {
      const selectedUid = profile.metadata.user_uid;
      navigate('/sourcing/profile', {
        state: {
          profile,
          searchState: {
            qualityVersion: 3,
            searchText,
            continuation,
            activeQuery,
            profiles,
            lebanonDescription,
            selectedUid,
          },
          startUnlock: true,
          justRanOut: false,
        },
      });
      return;
    }

    if (profile.metadata?.pinecone_id) {
      // Check if profile is already unlocked (already viewed)
      const profileKey = safeFirebaseKey(profile.metadata.pinecone_id);
      const isAlreadyUnlocked = viewedProfileIds.includes(profileKey);

      if (isAlreadyUnlocked) {
        // Already unlocked - navigate without calling unlock API (no credit deduction)
        const selectedUid = profile.metadata.user_uid;
        navigate('/sourcing/profile', {
          state: {
            profile: profile,
            searchState: {
              qualityVersion: 3,
              searchText,
              continuation,
              activeQuery,
              profiles,
              lebanonDescription,
              selectedUid,
            },
            justRanOut: false,
          },
        });
        return;
      }

      // Not yet unlocked - check if profile has contact info before deducting credits
      const hasContact = await hasContactInfo(profile);

      if (hasContact === false) {
        // DEFINITELY no contact info - navigate without unlocking (no credit deduction)
        console.log('[Unlock] Skipping credit deduction - no contact info found');
        const selectedUid = profile.metadata.user_uid;
        navigate('/sourcing/profile', {
          state: {
            profile: profile,
            searchState: {
              qualityVersion: 3,
              searchText,
              continuation,
              activeQuery,
              profiles,
              lebanonDescription,
              selectedUid,
            },
            justRanOut: false,
          },
        });
        return;
      }

      // Has contact info (true) OR uncertain (null) - proceed with unlock (credit deduction)
      console.log('[Unlock] Proceeding with unlock - contact check result:', hasContact);
      if (!accountUid) return;
      try {
        const response = await axios.post(
          `${API_BASE_URL}/v2/unlock_profile`,
          { profile_id: profile.metadata.pinecone_id },
          { params: { user_uid: accountUid } }
        );
        const { justRanOut } = response.data;
        const selectedUid = profile.metadata.user_uid;
        navigate('/sourcing/profile', {
          state: {
            profile: profile,
            searchState: {
              qualityVersion: 3,
              searchText,
              continuation,
              activeQuery,
              profiles,
              lebanonDescription,
              selectedUid,
            },
            justRanOut: justRanOut,
          },
        });
      } catch (error) {
        if (error.response && error.response.status === 402) {
          alert(uiText('No credits available. Please purchase more credits.'));
        } else {
          console.error('Error unlocking profile:', error);
        }
      }
    } else {
      navigate('/sourcing/profile', {
        state: {
          profile,
          searchState: {
            qualityVersion: 3,
            searchText,
            continuation,
            activeQuery,
            profiles,
            lebanonDescription,
            selectedUid: profile.metadata?.user_uid || '',
          },
          justRanOut: false,
        },
      });
    }
  };

  const handleDownloadList = async (profilesInShortlist) => {
    setIsDownloading(true);
    try {
      const emailPromises = profilesInShortlist.map(async (profile) => {
        const userUid = profile.uid;
        try {
          const response = await axios.post(
            'https://us-central1-jobseeqr.cloudfunctions.net/getUserEmail',
            { uid: userUid }
          );
          const email = response.data.email || 'No Email On File';
          return { userUid, email };
        } catch (error) {
          console.log('Error fetching user email for uid:', userUid, error);
          return { userUid, email: 'No Email On File' };
        }
      });
      const emailResults = await Promise.all(emailPromises);

      const data = profilesInShortlist.map((profile) => {
        const emailResult = emailResults.find((er) => er.userUid === profile.uid);
        const email = emailResult ? emailResult.email : 'No Email On File';
        return {
          Name: profile.metadata.username,
          Email: email,
          'Phone Number': profile.metadata.phoneNumber,
          'LinkedIn URL': profile.metadata.linkedin,
          Tags: (profile.metadata.tags || []).join(', '),
          'Key Achievements': profile.metadata.key_achievements || '',
          'Latest Experience': profile.metadata.experience || '',
          'Workplace Info': profile.metadata.company_info || '',
          Education: profile.metadata.education || '',
          'Institution Info': profile.metadata.institution_info || '',
        };
      });

      const csvContent = convertToCSV(data);
      downloadCSV(csvContent, `${selectedShortlist}.csv`);
    } catch (error) {
      console.error('Error downloading list:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const convertToCSV = (data) => {
    if (data.length === 0) return '';
    const csvRows = [];
    const headers = Object.keys(data[0]);
    csvRows.push(headers.join(','));

    for (const row of data) {
      const values = headers.map((header) => {
        let value = row[header];
        if (value === null || value === undefined) {
          value = '';
        }
        value = value.toString().replace(/"/g, '""');
        if (value.search(/("|,|\n)/g) >= 0) {
          value = '"' + value + '"';
        }
        return value;
      });
      csvRows.push(values.join(','));
    }

    return csvRows.join('\n');
  };

  const downloadCSV = (csvContent, filename) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    if (navigator.msSaveBlob) {
      navigator.msSaveBlob(blob, filename);
    } else {
      const link = document.createElement('a');
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  };

  const buttonLabel = !hasSearched || searchText.trim() === '' ? t('search.searching', 'Search') : t('search.filterResults', 'Filter Results');

  const handleManageSubscription = async () => {
    const functions = getFunctions();
    const createCustomerPortal = httpsCallable(functions, 'createCustomerPortalTSE');
    try {
      const response = await createCustomerPortal();
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Error accessing the customer portal:', error);
    }
  };

  // ==================== TUTORIAL IMPLEMENTATION ====================
  const startTSETutorial = useCallback(() => {
    const isArabic = isRTL || language === 'ar' || (i18n?.language || '').startsWith('ar');
    const tr = (en, ar) => (isArabic ? ar : en);
    const steps = [
      {
        popover: {
          title: tr('👋 Welcome to the Talent Search Engine!', '👋 أهلاً بك في محرك البحث عن المواهب'),
          description: tr(
            `
            Search through <strong>800M+ verified candidate profiles</strong> using AI-powered search.<br/><br/>
            <strong>Let's explore the key features!</strong>
          `,
            `
            ابحث في <strong>أكثر من 800M ملف مرشح موثّق</strong> باستخدام بحث مدعوم بالذكاء الاصطناعي.<br/><br/>
            <strong>لنستكشف المزايا الأساسية!</strong>
          `
          ),
        }
      },
      {
        element: '.search-box',
        popover: {
          title: tr('🔍 Smart Search', '🔍 بحث ذكي'),
          description: tr(
            `
            <strong>Two ways to search:</strong><br/>
            1. <strong>Type keywords</strong> - "Software Engineer with React experience"<br/>
            2. <strong>Paste a job description</strong> - AI finds the best matches<br/><br/>
            The AI understands context and finds the best matching profiles for you
          `,
            `
            <strong>طريقتان للبحث:</strong><br/>
            1. <strong>أدخل كلمات مفتاحية</strong> – مثل "مهندس برمجيات بخبرة React"<br/>
            2. <strong>ألصق وصف الوظيفة</strong> – والذكاء الاصطناعي سيجد أفضل المرشحين<br/><br/>
            الذكاء الاصطناعي يفهم السياق ويختار أنسب الملفات لك.
          `
          ),
          side: 'bottom',
          align: 'start'
        }
      },
      {
        element: '.profile-card',
        popover: {
          title: tr('👤 Profile Cards', '👤 بطاقات الملف الشخصي'),
          description: tr(
            `
            Each card shows:<br/>
            • Candidate name & headline<br/>
            • Experience summary<br/>
            • Skills & tags<br/>
            • "Open to Work" status<br/>
            • Ranking badge <br/><br/>
            <strong>Click any card</strong> to view full profile
          `,
            `
            كل بطاقة تعرض:<br/>
            • اسم المرشح والعنوان المهني<br/>
            • ملخص الخبرة<br/>
            • المهارات والوسوم<br/>
            • حالة "متاح للعمل"<br/>
            • شارة الترتيب<br/><br/>
            <strong>انقر على أي بطاقة</strong> لعرض الملف الكامل.
          `
          ),
          side: 'top',
          align: 'start'
        }
      },
      {
        element: '.hamburger-icon',
        popover: {
          title: tr('📋 Shortlists', '📋 القوائم المختصرة'),
          description: tr(
            `
            <strong>Save candidates into custom lists:</strong><br/>
            • Create multiple shortlists (e.g., "Top Engineers", "Final Round")<br/>
            • Add candidates to shortlists with one click<br/>
            • View and manage shortlisted profiles<br/><br/>
            <strong>Pro tip:</strong> Use credits wisely by unlocking only shortlisted candidates
          `,
            `
            <strong>احفظ المرشحين في قوائم مخصّصة:</strong><br/>
            • أنشئ عدة قوائم (مثل "أفضل المهندسين"، "المقابلة النهائية")<br/>
            • أضف المرشحين للقوائم بنقرة واحدة<br/>
            • اعرض وأدر الملفات المختارة<br/><br/>
            <strong>نصيحة:</strong> استخدم الأرصدة بحكمة وافتح ملفات المرشحين الموجودين في القوائم فقط.
          `
          ),
          side: 'left',
          align: 'start'
        }
      },
      {
        element: '.credits-display',
        popover: {
          title: tr('⚡ Credit System', '⚡ نظام الأرصدة'),
          description: tr(
            `
            <strong>How credits work:</strong><br/>
            • Each profile unlock costs 1 credit<br/>
            • Unlocking reveals full CV & contact info<br/>
            • Credits are shared across your team<br/>
            • Purchase more credits anytime<br/><br/>
            <strong>View unlocked profiles</strong> by clicking "View Unlocked Profiles"
          `,
            `
            <strong>كيف تعمل الأرصدة:</strong><br/>
            • فتح كل ملف يكلف 1 رصيد<br/>
            • الفتح يكشف السيرة الذاتية كاملة وبيانات التواصل<br/>
            • الأرصدة مشتركة مع فريقك<br/>
            • يمكنك شراء أرصدة إضافية في أي وقت<br/><br/>
            <strong>اعرض الملفات المفتوحة</strong> بالضغط على "View Unlocked Profiles".
          `
          ),
          side: 'bottom',
          align: 'start'
        }
      },
      {
        popover: {
          title: tr('🎉 TSE Complete!', '🎉 انتهى دليل محرك البحث'),
          description: tr(
            `
            <strong>You now know how to:</strong><br/>
            ✅ Search 800M+ profiles with AI<br/>
            ✅ View candidate cards & details<br/>
            ✅ Create and manage shortlists<br/>
            ✅ Unlock profiles with credits<br/><br/>
            <strong>Ready to see Onboarding?</strong><br/>
            <em>(Automate new hire onboarding workflow)</em>
          `,
            `
            <strong>أصبحت تعرف كيف:</strong><br/>
            ✅ تبحث في أكثر من 800M ملف عبر الذكاء الاصطناعي<br/>
            ✅ تعرض بطاقات المرشحين والتفاصيل<br/>
            ✅ تنشئ القوائم المختصرة وتديرها<br/>
            ✅ تفتح الملفات باستخدام الأرصدة<br/><br/>
            <strong>مستعد لعرض الإعداد الوظيفي؟</strong><br/>
            <em>(أتمتة مهام استقبال المنضمين الجدد)</em>
          `
          ),
        }
      }
    ];

    const driverObj = driver({
      showProgress: true,
      steps: steps,
      onDestroyed: async () => {
        // Save progress
        if (accountUid) {
          try {
            await set(ref(database, `companies/${accountUid}/tutorialProgress/tse`), true);
          } catch (error) {
            console.error('Error saving tutorial progress:', error);
          }
        }

        // Ask about Onboarding tutorial
        const continueToOnboarding = window.confirm(
          tr(
            "Ready to see the Onboarding module?\n\nAutomate your new hire onboarding with task tracking, document signing, and team assignments.\n\nTakes 60 seconds.",
            "هل أنت مستعد لرؤية قسم الإعداد الوظيفي؟\n\nأتمت مهام انضمام الموظفين الجدد مع تتبع المهام، توقيع المستندات، وتوزيع المسؤوليات.\n\nيستغرق 60 ثانية."
          )
        );

        if (continueToOnboarding) {
          window.location.href = '/employee-onboarding?startTutorial=true';
        }
      }
    });

    driverObj.drive();
  }, [accountUid]);

  // Auto-start tutorial if navigated from ATS
  useEffect(() => {
    if (!location?.state?.startTutorial || !accountUid) {
      return;
    }
    const timer = setTimeout(() => {
      startTSETutorial();
    }, 1500);
    // Clear the state
    window.history.replaceState({}, document.title);
    return () => clearTimeout(timer);
  }, [accountUid, location, startTSETutorial]);

  // Listen for tutorial trigger from ProductProtectedRoute
  useEffect(() => {
    const handleTutorialFromOverlay = (event) => {
      console.log('[TalentSearch] Received startTutorialFromOverlay event', event.detail);
      const { onTutorialComplete } = event.detail;

      // Start the tutorial
      startTSETutorial();

      // Note: startTSETutorial creates a new driver instance each time
      // The onTutorialComplete callback will be handled by ProductProtectedRoute
      // checking access after the overlay is dismissed
    };

    window.addEventListener('startTutorialFromOverlay', handleTutorialFromOverlay);
    return () => {
      window.removeEventListener('startTutorialFromOverlay', handleTutorialFromOverlay);
    };
  }, [startTSETutorial]);

  if (isShortlistsPopupOpen) return <ShortlistWorkspace accountUid={accountUid} lists={shortlists}
    selected={selectedShortlist} onSelect={selectShortlist} onBack={closeShortlists}
    loading={shortlistsLoading || !accountUid} loadError={shortlistsError} onRetry={() => { setShortlistsLoading(true); setShortlistsRetry(value => value + 1); }}
    onExport={handleDownloadList} exporting={isDownloading}
    onOpenProfile={profile => {
      if (profile.metadata?.connector_candidate_id) {
        navigate(`/talent/profile?shortlist_id=${encodeURIComponent(selectedShortlist)}&candidate_id=${encodeURIComponent(profile.metadata.connector_candidate_id)}&from=sourcing`);
      } else {
        navigate('/sourcing/profile', { state: { profile, startUnlock: false, searchState: {
          qualityVersion: 3, searchText, activeQuery, profiles, continuation,
          selectedShortlist, sourcingView: 'shortlists',
        } } });
      }
    }} />;

  return (
    <div className="talent-search-container">
      {creditsError && <button onClick={() => setCreditsRetry(value => value + 1)}>
        {t('credits.retry', 'Could not load credits. Retry')}
      </button>}
      {/* Top Menu Bar */}
      <div className="hamburger-menu">
        {creditsLoading && (
          <div className="credits-display" role="status">
            {t('credits.loading', 'Loading credits...')}
          </div>
        )}
        {!creditsLoading && !hasUnlimitedPlan && (
          <button className="manage-plan-btn" onClick={handleManageSubscription}>
            {t('managePlan', 'Manage Plan')}
          </button>
        )}
        {!creditsLoading && !creditsError && (
          <div className="credits-display">
            {hasUnlimitedPlan ? t('credits.unlimited', 'Unlimited Credits') : `${t('credits.title', 'Profile Credits')}: ${remainingCredits}`}
          </div>
        )}
        <i
          className="fa-solid fa-rectangle-list hamburger-icon"
          onClick={handleShortlistsClick}
          style={{ fontSize: '30px', cursor: 'pointer' }}
          title={t('shortlists.title', 'View Shortlists')}
        ></i>
      </div>

      {/* Zero Credits Popup */}
      {(!creditsLoading && showZeroCreditsPopup) && (
        <div className="zero-credits-popup">
          <div className="popup-content">
            <h2>{t('zeroCredits.title', "You've run out of credits!")}</h2>
            <p>{t('zeroCredits.description', 'Consider purchasing more credits or upgrading your subscription.')}</p>
            <button onClick={() => setShowZeroCreditsPopup(false)}>{t('zeroCredits.close', 'Close')}</button>
          </div>
        </div>
      )}

      {/* Search Section */}
      {!isLoading && currentView === 'search' && profiles.length === 0 && !continuation && (
        <SearchStart inputRef={starterTextareaRef} searchText={searchText} onQueryChange={setSearchText}
          onSearch={handleSearch} onSuggestion={handleSuggestionClick} suggestions={suggestions}
          hasSearched={hasSearched} buttonLabel={buttonLabel} isRTL={isRTL} />
      )}

      {/* Loading Section */}
      {isLoading && (
        <div className="loading-section">
          <div className="loading-animation"></div>
          <h2 className="loading-text">
            {t('search.loadingText', 'Searching For Profiles')}<span className="dots">...</span>
          </h2>
        </div>
      )}

      {/* Results Section */}
      {!isLoading && currentView === 'search' && (profiles.length > 0 || continuation) && (
        <div className="results-section">
          <div className="results-header">
            <h2 className="subtitle">{t('results.showing', 'Showing {{count}} results', { count: profiles.length })}</h2>
            {/* <h2 className="subtitle">Showing 500+ results</h2> */}
            <div className="toggle-container-tse">
              <span className="toggle-label-tse">{t('results.hideViewed', 'Hide viewed profiles')}</span>
              <label className="switch-tse">
                <input
                  type="checkbox"
                  checked={hideViewed}
                  onChange={(e) => setHideViewed(e.target.checked)}
                />
                <span className="slider-tse"></span>
              </label>
            </div>
          </div>

          {/* <p className="description">{lebanonDescription || 'Loading description...'}</p> */}

          {(() => {
            const filteredProfiles = hideViewed
              ? profiles.filter(profile => !viewedProfileIds.includes(safeFirebaseKey(profile.metadata.pinecone_id)))
              : profiles;
            return (
              <div className="profile-navigation" ref={scrollRef}>
                <div className="gallery">
                  {filteredProfiles.map((profile, index) => {
                    const { badgeLabel, badgeColor } = getRankingBadgeProps(index);
                    return (
                      <div
                        key={index}
                        id={profile.metadata.pinecone_id}
                        className="profile-card"
                        onClick={() => handleProfileClick(profile)}
                      >
                        {/* Render the eye icon if the profile has been viewed */}
                        {viewedProfileIds.includes(safeFirebaseKey(profile.metadata.pinecone_id)) && (
                          <i className="fa-solid fa-eye viewed-icon"></i>
                        )}
                        {/* Ranking badge hidden for now */}
                        {false && (
                          <div
                            className="ranking-badge"
                            style={{ backgroundColor: badgeColor }}
                          >
                            {badgeLabel}
                          </div>
                        )}
                        <p className="personName">{profile.metadata.username}</p>
                        <p className="profile-card-info">
                          <TalentPosition metadata={profile.metadata} language={language} />
                        </p>
                        <TalentMatches.Badges metadata={profile.metadata} t={t} />
                      </div>
                    );
                  })}
                </div>
                <div ref={moreResultsRef} className="tse-more-results" aria-live="polite">
                  {loadingMore && <span>{t('search.loadingText', 'Searching For Profiles')}...</span>}
                  {pageError && <>
                    <span role="alert">{pageError}</span>
                    <button type="button" onClick={loadNextPage}>{t('results.retry', 'Retry')}</button>
                  </>}
                </div>
              </div>
            );
          })()}

          <div className={`search-box-card search-box-bottom ${isFilterBoxExpanded ? 'is-expanded' : ''}`}>
            <textarea
              className={`search-box-input search-box search-box-filter ${isFilterBoxExpanded ? 'is-expanded' : ''}`}
              placeholder={t('search.placeholder', 'Paste a full job description for best results. Alternatively, list the skills & requirements you\'re looking for.')}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <div className="search-box-footer">
              <button
                type="button"
                className="search-expand-toggle-btn"
                onClick={() => setIsFilterBoxExpanded(prev => !prev)}
                title={isFilterBoxExpanded ? t('search.collapseBox', 'Collapse text area') : t('search.expandBox', 'Expand text area')}
                aria-label={isFilterBoxExpanded ? t('search.collapseBox', 'Collapse text area') : t('search.expandBox', 'Expand text area')}
              >
                <i className={`fa-solid ${isFilterBoxExpanded ? 'fa-compress' : 'fa-expand'}`}></i>
                <span className="expand-btn-text">
                  {isFilterBoxExpanded ? t('search.collapse', 'Collapse') : t('search.expand', 'Expand')}
                </span>
              </button>
              <button className="search-button" onClick={handleSearch}>
                {buttonLabel && <span className="button-text">{buttonLabel}</span>}
                <i className={`fa-solid ${isRTL ? 'fa-arrow-left' : 'fa-arrow-right'}`}></i>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unlocked Profiles */}
      {!isLoading && currentView === 'unlocked' && (
        <UnlockedProfiles
          unlockedProfiles={unlockedProfiles}
          remainingCredits={remainingCredits}
          onBackToSearch={() => navigate('/sourcing?view=search', { state: location.state })}
          onProfileClick={handleProfileClick}
        />
      )}

    </div>
  );
};

export default TalentSearch;
